import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BrandmarkHorizontal } from "@/components/Brandmark";
import { List, X } from "@phosphor-icons/react";
import { useState } from "react";

// Public-page navigation. Shared across Landing / About / Pricing / Contact / Terms
// so the marketing nav stays consistent. Constrained to 1600px max width with
// 1rem (16px) horizontal padding per the iter-24 spec.
const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

export default function PublicNav() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const linkCls = (to) =>
    `text-sm font-medium tracking-tight transition-colors ${
      pathname === to
        ? "text-slate-950"
        : "text-slate-600 hover:text-slate-950"
    }`;

  return (
    <nav className="w-full bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-40">
      <div className="mx-auto max-w-[1600px] px-4 h-16 flex items-center justify-between gap-6">
        {/* Brand */}
        <Link
          to="/"
          className="flex items-center shrink-0"
          data-testid="public-nav-brand"
        >
          <BrandmarkHorizontal height={32} />
        </Link>

        {/* Desktop links — centered */}
        <div className="hidden md:flex items-center gap-8 flex-1 justify-center">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={linkCls(l.to)}
              data-testid={`public-nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        {/* CTAs — right */}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          <Link
            to="/login"
            className="text-sm font-medium text-slate-700 hover:text-slate-950 transition-colors"
            data-testid="public-nav-login"
          >
            Log in
          </Link>
          <Link to="/register">
            <Button
              className="h-10 px-5 rounded-full text-white font-medium text-sm tg-gradient hover:opacity-95 tg-shadow-soft border-0"
              data-testid="public-nav-cta"
            >
              Get Started
            </Button>
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="md:hidden h-10 w-10 rounded-full hover:bg-slate-100 flex items-center justify-center"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="public-nav-mobile-toggle"
        >
          {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-3" data-testid="public-nav-mobile-panel">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={`block ${linkCls(l.to)}`}
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-slate-700"
            >
              Log in
            </Link>
            <Link to="/register" onClick={() => setOpen(false)}>
              <Button className="w-full h-10 rounded-full text-white tg-gradient border-0">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
